import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/statusUtils";
import { toast } from "sonner";
import { RefreshCw, Search, Eye, Lock } from "lucide-react";

const ACTION_META = {
  user_login: { label: "Login", color: "#347355" },
  user_login_failed: { label: "Login failed", color: "#DC2626" },
  user_register: { label: "Register", color: "#5C6653" },
  user_create: { label: "User created", color: "#234B36" },
  user_update: { label: "User updated", color: "#234B36" },
  user_delete: { label: "User deleted", color: "#DC2626" },
  user_approve: { label: "User approved", color: "#347355" },
  user_reject: { label: "User rejected", color: "#DC2626" },
  plot_create: { label: "Plot created", color: "#234B36" },
  plot_update: { label: "Plot updated", color: "#234B36" },
  plot_delete: { label: "Plot deleted", color: "#DC2626" },
  plot_status_change: { label: "Plot status change (super_admin)", color: "#C06C47" },
  lp_update: { label: "LP updated", color: "#234B36" },
  lp_layout_upload: { label: "Layout uploaded", color: "#234B36" },
  booking_create: { label: "Booking created", color: "#2C5E7A" },
  booking_update: { label: "Booking updated", color: "#234B36" },
  booking_approve: { label: "Booking approved", color: "#347355" },
  booking_reject: { label: "Booking rejected", color: "#DC2626" },
  booking_delete: { label: "Booking deleted", color: "#DC2626" },
  user_suspend: { label: "User suspended", color: "#EA580C" },
  user_reactivate: { label: "User reactivated", color: "#347355" },
  cp_assign: { label: "CP assigned to Admin", color: "#234B36" },
  cp_reassign: { label: "CP reassigned to Admin", color: "#C06C47" },
  cp_unassign: { label: "CP unassigned", color: "#5C6653" },
};

export default function AdminAudit() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [resourceFilter, setResourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter !== "all") params.action = actionFilter;
      if (resourceFilter !== "all") params.resource_type = resourceFilter;
      const [l, s] = await Promise.all([
        api.get("/audit/logs", { params }),
        api.get("/audit/stats"),
      ]);
      setLogs(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter, resourceFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((row) =>
      (row.actor_email || "").toLowerCase().includes(q) ||
      (row.actor_name || "").toLowerCase().includes(q) ||
      (row.resource_id || "").toLowerCase().includes(q) ||
      JSON.stringify(row.details || {}).toLowerCase().includes(q)
    );
  }, [logs, search]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Admin</div>
            <h1 className="font-heading text-2xl md:text-4xl">Audit Log</h1>
            <p className="text-sm text-[#5C6653] mt-2">
              {isSuper
                ? "Every sensitive action across the portal — who did what, when."
                : "Your own portal activity. Actions taken by other admins and super admins are private."}
            </p>
            {!isSuper && (
              <div data-testid="audit-scope-notice" className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-sm bg-[#F0EFEA] border border-[#E5E2DC] text-[#5C6653]">
                <Lock className="h-3 w-3" /> Scope: your account only
              </div>
            )}
          </div>
          <Button onClick={load} variant="outline" className="rounded-sm w-fit" data-testid="refresh-audit-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className={`grid grid-cols-1 ${isSuper ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4 mb-6`} data-testid="audit-stats">
            <div className="border border-[#E5E2DC] bg-white p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold">{isSuper ? "Total Events" : "Your Events"}</div>
              <div className="font-heading text-3xl mt-2">{stats.total.toLocaleString()}</div>
            </div>
            <div className="border border-[#E5E2DC] bg-white p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold mb-2">Top Actions</div>
              <div className="space-y-1">
                {stats.top_actions.slice(0, 5).map((a) => (
                  <div key={a.action} className="flex justify-between text-xs">
                    <span>{ACTION_META[a.action]?.label || a.action}</span>
                    <span className="font-mono-anu text-[#5C6653]">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
            {isSuper && (
              <div className="border border-[#E5E2DC] bg-white p-5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold mb-2">Most Active</div>
                <div className="space-y-1">
                  {stats.top_actors.slice(0, 5).map((a) => (
                    <div key={a.actor_email} className="flex justify-between text-xs">
                      <span className="truncate max-w-[180px]">{a.actor_email}</span>
                      <span className="font-mono-anu text-[#5C6653]">{a.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="border border-[#E5E2DC] bg-white p-4 flex flex-wrap gap-3 mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6653]" />
            <Input
              placeholder="Search actor / details"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="audit-search-input"
              className="pl-9 w-full sm:w-64 rounded-sm"
            />
          </div>
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-full sm:w-44 rounded-sm" data-testid="audit-resource-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="plot">Plot</SelectItem>
              <SelectItem value="lp">LP</SelectItem>
              <SelectItem value="booking">Booking</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-full sm:w-56 rounded-sm" data-testid="audit-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.entries(ACTION_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-[#5C6653] self-center whitespace-nowrap">
            <span className="font-mono-anu text-[#1A1F16]">{filtered.length}</span> events
          </div>
        </div>

        <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid="audit-log-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Timestamp</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Resource</Th>
                <Th>Details</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-[#5C6653]">No events match your filters.</td></tr>
              ) : (
                filtered.map((row) => {
                  const meta = ACTION_META[row.action] || { label: row.action, color: "#5C6653" };
                  return (
                    <tr key={row.id} className="border-b border-[#E5E2DC] hover:bg-[#F9F8F6]" data-testid={`audit-row-${row.id}`}>
                      <Td>
                        <span className="text-xs text-[#5C6653] font-mono-anu whitespace-nowrap">{formatDate(row.timestamp)}</span>
                      </Td>
                      <Td>
                        {row.actor_name ? (
                          <>
                            <div className="font-medium">{row.actor_name}</div>
                            <div className="text-xs text-[#5C6653]">{row.actor_email}</div>
                          </>
                        ) : <span className="text-xs text-[#5C6653]">System / anonymous</span>}
                      </Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium whitespace-nowrap" style={{ background: `${meta.color}15`, borderColor: meta.color, color: meta.color }}>
                          {meta.label}
                        </span>
                      </Td>
                      <Td>
                        <span className="text-xs uppercase tracking-wider text-[#5C6653]">{row.resource_type}</span>
                        {row.resource_id && <div className="text-[10px] font-mono-anu text-[#5C6653] truncate max-w-[180px]">{row.resource_id.slice(0, 12)}…</div>}
                      </Td>
                      <Td>
                        <span className="text-xs text-[#5C6653] truncate max-w-[280px] block">
                          {row.details && Object.keys(row.details).length > 0
                            ? Object.entries(row.details).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")
                            : "—"}
                        </span>
                      </Td>
                      <Td>
                        <button onClick={() => setViewing(row)} data-testid={`audit-view-${row.id}`} className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm">
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Audit event</DialogTitle>
              </DialogHeader>
              <div className="space-y-1 text-sm">
                <R k="Timestamp" v={formatDate(viewing.timestamp)} />
                <R k="Action" v={ACTION_META[viewing.action]?.label || viewing.action} />
                <R k="Actor" v={viewing.actor_name || "System"} />
                <R k="Actor email" v={viewing.actor_email || "—"} />
                <R k="Actor role" v={viewing.actor_role || "—"} />
                <R k="Resource" v={viewing.resource_type} />
                <R k="Resource ID" v={viewing.resource_id || "—"} mono />
                <div className="py-2">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1">Details</div>
                  <pre className="text-xs bg-[#F9F8F6] p-3 rounded-sm border border-[#E5E2DC] overflow-auto">{JSON.stringify(viewing.details || {}, null, 2)}</pre>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function R({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1 border-b border-[#E5E2DC] last:border-0">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold col-span-1 self-center">{k}</div>
      <div className={`col-span-2 text-sm ${mono ? "font-mono-anu text-xs" : ""}`}>{v}</div>
    </div>
  );
}
